import React from "react";
import { type NextPage } from "next";
import { Box, SimpleGrid, Text } from "@chakra-ui/react";
import { Page } from "components/page";
import { Section, sectionSpacingY } from "components/section";
import { StackedLogos } from "components/logos";
import { PricingHeroBg, Faq, PricingDetails, PricingPlans } from "components/pricing";
import { getLandingPageProps, getPageMetaImage, type LandingPageProps } from "utils";
import { TextColumn, TextTitle } from "ui/text";
import { Container } from "ui/container";
import { AuraCardQuote } from "components/quotes";
import { LeversIcon, MouseIcon, TrendIcon } from "ui/icons";
import Balancer from "react-wrap-balancer";

const Pricing: NextPage<LandingPageProps> = ({ page, announcementBanner, defaultLogos, defaultMobileLogos }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      bgColor="new.blue.150"
      footerOptions={{ variant: "blue" }}
    >
      <Box as="section" pos="relative" pt={{ base: "big", lg: "biggest" }}>
        <PricingHeroBg />
        <Container pos="relative">
          <TextTitle
            align="center"
            mobileAlign="center"
            size="xl"
            titleAs="h1"
            chip={{ text: page.eyebrow }}
            title={<Balancer>{page.title}</Balancer>}
          />

          <PricingPlans />
        </Container>
      </Box>

      <StackedLogos customers={defaultLogos} mobileCustomers={defaultMobileLogos} mb={sectionSpacingY} />

      <Section>
        <TextTitle
          size="lg"
          align="center"
          mobileAlign="center"
          title="Pricing built to scale with your business"
          maxW={{ base: "md", md: "xl", lg: "100%" }}
        />

        <SimpleGrid
          columns={{ base: 1, md: 3 }}
          gap={{ base: "biggest", md: "big", lg: "biggest" }}
          mt={{ base: "biggest", md: "huge" }}
          maxW={{ base: "sm", md: "100%" }}
          mx="auto"
        >
          <TextColumn
            iconChip={{ variant: "violet", icon: LeversIcon }}
            title="Composable"
            description="Mix and match products to create a solution tailored to your specific needs. Only pay for what you use. No wasted spend on features you don’t need."
          />
          <TextColumn
            iconChip={{ variant: "blue", icon: MouseIcon }}
            title="Usage-based"
            description="Get charged based on actual usage. No MTU (Monthly Tracked Users) limits, and no caps on sources, destinations, or user seats."
          />
          <TextColumn
            iconChip={{ variant: "yellow", icon: TrendIcon }}
            title="Economies of scale"
            description="As your usage grows, your unit costs shrink. Our pricing is designed to scale with you, delivering more value as your business expands."
          />
        </SimpleGrid>
      </Section>

      <AuraCardQuote
        variant="blue"
        quote={{
          maxW: "970px",
          quoteText: (
            <>
              One of the big value props of Hightouch is{" "}
              <Text as="strong" fontWeight="bold">
                time-to-value.
              </Text>{" "}
              We’ve seen a lot of value very quickly.
            </>
          ),
          author: { name: "Natalie Miles", job: "Head of Marketing Technology and Personalization" },
          company: {
            name: "Chime",
            logo: "https://cdn.sanity.io/images/pwmfmi47/production/0bfab275e65467db361389702dbb62cb2f01b24c-1106x316.png",
            logoRatio: 5 / 1,
          },
        }}
      />

      <PricingDetails />

      <Faq />
    </Page>
  );
};

export default Pricing;

export const getStaticProps = getLandingPageProps("/pricing", {
  loadDefaultLogos: true,
  loadDefaultMobileLogos: true,
});
